import setHandlerViewState from '../actions/setHandlerViewState';
import AugLoopPluginUI from '../components/AugLoopPluginUI';
import getViewStateForAnnotationHandler from '../selectors/getViewStateForAnnotationHandler';
import { ComposeScenario } from '../store/schema/ComposeScenario';
import collapseParentPath from '../utils/collapseParentPath';
import { AddOperation, IOperation, Item, UpdateOperation } from '@augloop/types-core';
import { FormattedTextTile } from '@augloop/types-text';
import { MailDocument } from '@augloop-types/mail';
import { addFlightInformationToConstraintsEnUs } from 'owa-editor-smartcompose-plugin/lib/utils/addFlightInformationToConstraints';
import { addIntentDetectionFlightConstraintsEnUs } from 'owa-editor-smartcompose-plugin/lib/utils/addIntentDetectionFlightConstraints';
import {
    Envelope as SmartComposeEnvelope,
    Recipient,
    MailboxType,
} from '@augloop-types/smart-compose';
import type EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import { getUserDisplayName, getUserEmailAddress } from 'owa-session-store';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { AugloopSession, lazyDisposeAugLoopSession, lazyGetAugLoopSession } from 'augloop-service';
import { ObservableMap } from 'mobx';
import { logUsage } from 'owa-analytics';
import getCharactersBeforeCursor from 'owa-editor-augloop-plugin-utils/lib/getCharactersBeforeCursor';
import isKeycodePrintable from 'owa-editor-augloop-plugin-utils/lib/isKeycodePrintable';
import PluginNames from 'owa-editor-lazy-plugin/lib/utils/PluginNames';
import { getGuid } from 'owa-guid';
import * as trace from 'owa-trace';
import { IEditor, PluginEvent, PluginEventType } from 'roosterjs-editor-types';
import type {
    default as AnnotationHandler,
    GetAnnotationHandlerAsync,
} from '../store/schema/AnnotationHandler';
import type AugLoopViewState from '../store/schema/AugLoopViewState';
import type Envelope from '../store/schema/Envelope';
import type { ISession } from '@augloop/runtime-client';
import type PluginUtils from 'owa-editor-plugin-utils/lib/schema/PluginUtils';
import type EditorPluginEx from 'owa-editor-plugin-utils/lib/schema/EditorPluginEx';
import type { PluginViewStateAdapter, PluginWithUI, EditorViewState } from 'owa-editor';
import type { SonoraViewState } from 'owa-editor-sonora-types';
import type Recipients from '../store/schema/Recipients';
import setSonoraViewState from '../actions/setSonoraViewState';
import { isFeatureEnabled } from 'owa-feature-flags';

const INITIAL_UPDATE_CHARACTER_THRESHOLD = 10;
const LONG_EMAIL_UPDATE_CHARACTER_THRESHOLD = 50;
const INITIAL_TIME_BEFORE_UPDATE = 100;
const LONG_EMAIL_TIME_BEFORE_UPDATE = 500;
const LONG_EMAIL_CHARACTER_THRESHOLD = 1000;

const DELETE_OPERATION = 'AugLoop_Core_DeleteOperation';
const ADD_OPERATION = 'AugLoop_Core_AddOperation';
const MOVE_OPERATION = 'AugLoop_Core_MoveOperation';
const UPDATE_OPERATION = 'AugLoop_Core_UpdateOperation';

const ANNOTATION_ACTIVATION_ERROR = 'ALAnnotationActivationError';
const SUBMIT_STRATEGY_EVENT_ERROR = 'ALSubmitStrategyEventError';
const HANDLER_EVENT_ERROR = 'ALHandlerEventError';
const ANNOTATION_PROCESS_ERROR = 'ALAnnotationProcessError';
const SESSION_INITIALIZATION_ERROR = 'AugLoopSessionInitializationError';

export default class AugLoopPlugin implements PluginWithUI<AugLoopViewState>, EditorPluginEx {
    private editor: IEditor;
    private pluginUtils: PluginUtils;
    private augLoopSession: ISession;
    private annotationHandlers: AnnotationHandler[];
    private bodyTileId: string;
    private currentAnnotationItems: ObservableMap<string, Item>;
    private charactersTypedSinceLastAugLoopUpdate: number;
    private updateTimer: NodeJS.Timer;
    private mostRecentTextContentLength = 0;
    private charactersBeforeCursor: string;
    private envelope: Envelope;
    private shouldSubmitGenericTextTile: boolean;
    private domEventHandlerdisposer: () => void;

    public getName() {
        return PluginNames.AugLoop;
    }

    constructor(
        public readonly adapter: PluginViewStateAdapter<AugLoopViewState>,
        public readonly editorAdapter: PluginViewStateAdapter<EditorViewState>,
        private readonly augloopSessionAdapter: PluginViewStateAdapter<AugloopSession>,
        sonoraAdapter: PluginViewStateAdapter<SonoraViewState>,
        private annotationHandlerGetters: GetAnnotationHandlerAsync[],
        private getRecipients: () => Recipients,
        private getSubject: () => string,
        private getEmailReferenceId: () => string,
        private composeOperation: 'Edit' | 'New',
        private scenario: ComposeScenario
    ) {
        this.updateEnvelope();
        setSonoraViewState(adapter(), sonoraAdapter());

        // For now, no handlers use the generic text tiles, so disable it.
        // If a handler needs to generic text tiles, re-enabled it with this switch
        this.shouldSubmitGenericTextTile = false;
    }

    /**
     * Initializes the plugin
     * @param editor The editor instance that is loading the plugin
     */
    public initialize(editor: IEditor) {
        this.editor = editor;
        trace.trace.info('[AugLoopPlugin] initialize: ' + this.editor);
        this.initializeAugmentationLoop().then(succeeded => {
            if (succeeded && this.annotationHandlerGetters) {
                this.annotationHandlerGetters.forEach(annotationHandlerGetter => {
                    annotationHandlerGetter().then(annotationHandler => {
                        const { onEditorEvent } = annotationHandler;
                        if (this.editor && this.augLoopSession) {
                            const viewState = this.adapter();
                            const handlerViewState = annotationHandler.initializeViewState(
                                this.composeOperation,
                                viewState.editorClientId,
                                this.augLoopSession,
                                this.editor,
                                viewState.composeFeedbackViewState,
                                viewState.sonoraViewState
                            );
                            if (onEditorEvent) {
                                this.domEventHandlerdisposer = this.editor.addDomEventHandler({
                                    focus: () => {
                                        this.handleFocus();
                                        onEditorEvent(handlerViewState, 'focus');
                                    },
                                    blur: () => onEditorEvent(handlerViewState, 'blur'),
                                });
                            } else {
                                this.domEventHandlerdisposer = this.editor.addDomEventHandler({
                                    focus: this.handleFocus,
                                });
                            }
                            setHandlerViewState(viewState, annotationHandler, handlerViewState);
                            this.addAnnotationHandler(annotationHandler);

                            const submitStrategy = annotationHandler.contentSubmitStrategy;
                            if (submitStrategy?.getWarmupOperation) {
                                const warmupOperation = submitStrategy?.getWarmupOperation(
                                    this.envelope
                                );
                                if (warmupOperation) {
                                    this.augLoopSession.submitOperation(warmupOperation);
                                }
                            }
                        }
                    });
                });
            }
        });
    }

    public getUIComponentClass() {
        return AugLoopPluginUI;
    }

    private async initializeAugmentationLoop(): Promise<boolean> {
        this.annotationHandlers = [];
        this.charactersTypedSinceLastAugLoopUpdate = 0;
        this.currentAnnotationItems = new ObservableMap<string, Item>();
        const getAugLoopSession = await lazyGetAugLoopSession.import();
        this.augLoopSession = await getAugLoopSession(this.augloopSessionAdapter().sessionId);

        if (this.augLoopSession && this.editor) {
            trace.trace.info('[AugLoopPlugin] Session initialized');
            return Promise.resolve(true);
        } else {
            trace.trace.info('[AugLoopPlugin] Session initialization failed');
            logUsage(SESSION_INITIALIZATION_ERROR, { logEvery: 1 });
        }
        return Promise.resolve(false);
    }

    private handleFocus = () => {
        trace.trace.info('[AugLoopPlugin] Editor got focus. Updating envelope');
        this.updateEnvelope();
    };

    private addAnnotationHandler(annotationHandler: AnnotationHandler) {
        this.annotationHandlers.push(annotationHandler);
        let annotationType = annotationHandler.getAnnotationType();
        let activationPromise = this.augLoopSession.activateAnnotation(annotationType, {
            callback: this.onAnnotationResult,
        });

        activationPromise.catch(error => {
            logUsage(ANNOTATION_ACTIVATION_ERROR, { error: error.message }, { logEvery: 1 });
            trace.trace.info(
                `[AugLoopPlugin] exception encountered when activating ${annotationType} annotation: ${error.message}`
            );
        });
    }

    public dispose() {
        const viewState = this.adapter();
        this.annotationHandlers.forEach(annotationHandler => {
            annotationHandler.onSessionEnd(
                getViewStateForAnnotationHandler(annotationHandler, viewState)
            );
        });

        this.editor = null;
        if (this.augLoopSession) {
            lazyDisposeAugLoopSession.importAndExecute(this.augloopSessionAdapter().sessionId);
            this.augLoopSession = null;
        }

        if (this.domEventHandlerdisposer) {
            this.domEventHandlerdisposer();
            this.domEventHandlerdisposer = null;
        }
    }

    public onPluginEvent(event: PluginEvent) {
        if (event.eventType == PluginEventType.ContentChanged) {
            trace.trace.info('[AugLoopPlugin] ContentChanged event');
            this.submitTextTileAndResetCounter();
        }

        this.updateCharactersBeforeCursor(event);

        // Check submit strategies and execute any who care about this event
        try {
            this.processSubmitStrategiesForEvent(event);
        } catch (error) {
            logUsage(SUBMIT_STRATEGY_EVENT_ERROR, { error: error.message }, { logEvery: 1 });
        }

        if (event.eventType === PluginEventType.KeyUp) {
            const keyboardEvent = event.rawEvent as KeyboardEvent;
            clearTimeout(this.updateTimer);
            this.updateTimer = setTimeout(() => {
                this.submitTextTileAndResetCounter();
            }, this.getTimeBeforeUpdateBasedOnMostRecentEmailLength());

            if (
                keyboardEvent.keyCode === KeyCodes.period ||
                keyboardEvent.keyCode === KeyCodes.enter ||
                keyboardEvent.keyCode === KeyCodes.del
            ) {
                this.submitTextTileAndResetCounter();
            } else {
                this.charactersTypedSinceLastAugLoopUpdate++;
                if (
                    this.charactersTypedSinceLastAugLoopUpdate >=
                    this.getCharactersBeforeUpdateBasedOnMostRecentEmailLength()
                ) {
                    this.submitTextTileAndResetCounter();
                }
            }
        }

        try {
            this.processPluginEvent(event);
        } catch (error) {
            logUsage(HANDLER_EVENT_ERROR, { error: error.message }, { logEvery: 1 });
        }
    }

    private processSubmitStrategiesForEvent(event: PluginEvent): void {
        if (this.charactersBeforeCursor && this.charactersBeforeCursor.slice(-2).trim() === '') {
            trace.trace.info('Multiple spaces detected before cursor, not sending signal to AL');
            return;
        }

        const viewState = this.adapter();
        this.annotationHandlers.forEach(annotationHandler => {
            const submitStrategy = annotationHandler.contentSubmitStrategy;
            if (submitStrategy?.shouldTrigger(event)) {
                const viewStateForAnnotationHandler = getViewStateForAnnotationHandler(
                    annotationHandler,
                    viewState
                );
                if (viewStateForAnnotationHandler) {
                    const operation = submitStrategy.onTrigger(
                        viewStateForAnnotationHandler,
                        event,
                        this.charactersBeforeCursor,
                        this.envelope
                    );
                    if (operation) {
                        this.augLoopSession.submitOperation(operation);
                    }
                }
            }
        });
    }

    private async updateCharactersBeforeCursor(event: PluginEvent): Promise<void> {
        if (event.eventType === PluginEventType.KeyDown) {
            const keyboardEvent = event.rawEvent as KeyboardEvent;

            // If the event is part of a composition, reset the buffer, and return.
            if (keyboardEvent.isComposing || !isKeycodePrintable(keyboardEvent.keyCode)) {
                this.charactersBeforeCursor = null;
            }

            if (keyboardEvent.keyCode === KeyCodes.backspace) {
                if (keyboardEvent.ctrlKey || keyboardEvent.metaKey || keyboardEvent.altKey) {
                    this.charactersBeforeCursor = null;
                } else {
                    this.charactersBeforeCursor = getCharactersBeforeCursor(this.editor)?.slice(
                        0,
                        -1
                    );
                    return;
                }
            } else if (!this.charactersBeforeCursor) {
                this.getCharactersBeforeCursorWithDelay().then(characters => {
                    this.charactersBeforeCursor = characters;
                });
            } else {
                this.charactersBeforeCursor += keyboardEvent.key;
            }
        } else if (
            event.eventType === PluginEventType.MouseDown ||
            event.eventType === PluginEventType.ContentChanged ||
            event.eventType === PluginEventType.BeforePaste
        ) {
            this.charactersBeforeCursor = getCharactersBeforeCursor(this.editor);
        }
    }

    private getCharactersBeforeCursorWithDelay(): Promise<string> {
        return new Promise(resolve => {
            setTimeout(() => {
                resolve(getCharactersBeforeCursor(this.editor));
            }, 1);
        });
    }

    private getTimeBeforeUpdateBasedOnMostRecentEmailLength(): number {
        if (this.mostRecentTextContentLength < LONG_EMAIL_CHARACTER_THRESHOLD) {
            return INITIAL_TIME_BEFORE_UPDATE;
        } else {
            return LONG_EMAIL_TIME_BEFORE_UPDATE;
        }
    }

    private getCharactersBeforeUpdateBasedOnMostRecentEmailLength(): number {
        if (this.mostRecentTextContentLength < LONG_EMAIL_CHARACTER_THRESHOLD) {
            return INITIAL_UPDATE_CHARACTER_THRESHOLD;
        } else {
            return LONG_EMAIL_UPDATE_CHARACTER_THRESHOLD;
        }
    }

    private submitTextTileAndResetCounter() {
        if (this.shouldSubmitGenericTextTile) {
            this.submitTextTile();
        }

        this.charactersTypedSinceLastAugLoopUpdate = 0;
    }

    public willHandleEventExclusively(event: PluginEvent) {
        return this.annotationHandlers.some(annotationHandler => {
            const viewState = getViewStateForAnnotationHandler(annotationHandler, this.adapter());
            if (viewState) {
                return annotationHandler.handlePluginEventExclusively(event, viewState);
            }
            return false;
        });
    }

    public setPluginUtils(pluginUtils: PluginUtils) {
        this.pluginUtils = pluginUtils;
        trace.trace.info('[AugLoopPlugin] setPluginUtils: ' + this.pluginUtils);
    }

    private onAnnotationResult = (operation: IOperation): void => {
        if (operation?.items) {
            // go through the list of operation items from this annotation result.
            // Each operation can do things like add, modify, or delete annotations, so as
            // we process the operations update out list of annotations
            operation.items.forEach(item => {
                switch (operation.H_.T_) {
                    case DELETE_OPERATION:
                        this.currentAnnotationItems.delete(item.id);
                        break;
                    case ADD_OPERATION:
                    case UPDATE_OPERATION:
                        this.currentAnnotationItems.set(item.id, {
                            id: item.id,
                            source: item.source,
                            revId: operation.parentRevId,
                            body: item.body,
                        });
                        break;
                    case MOVE_OPERATION:
                        // not implemented. No-op for now
                        break;
                }
            });

            // Once our list of annotations is updated send them to the annotation handlers to be processed
            // This is done in 3 steps since some annotationHandlers may have logic that needs to be ran before
            // or after all of the annotations are processed
            const parentPath = collapseParentPath(operation.parentPath);

            try {
                this.runAnnotationHandlerPreProcessors(parentPath);
                this.processAnnotations(parentPath);
                this.runAnnotationHandlerPostProcessors(parentPath);
            } catch (error) {
                logUsage(ANNOTATION_PROCESS_ERROR, { error: error.message }, { logEvery: 1 });
            }
        }
    };

    /********* PROCESS ANNOTATIONS *************/
    // Have each annotation handler run its pre-processor before processing any annotations
    private runAnnotationHandlerPreProcessors(parentPath: string) {
        this.annotationHandlers.forEach(annotationHandler => {
            annotationHandler.onOperationStart(parentPath, this.editor);
        });
    }

    // Have each annotation handler process each of the current annotations
    private processAnnotations(parentPath: string) {
        this.currentAnnotationItems.forEach(item => {
            this.annotationHandlers.forEach(annotationHandler => {
                if (annotationHandler.getAnnotationSubTypes().includes(item.source)) {
                    const viewState = getViewStateForAnnotationHandler(
                        annotationHandler,
                        this.adapter()
                    );
                    if (viewState) {
                        annotationHandler.handleAnnotation(
                            item,
                            parentPath,
                            this.editor,
                            viewState,
                            this.scenario
                        );
                    }
                }
            });
        });
    }

    private processPluginEvent(event: PluginEvent) {
        this.annotationHandlers.forEach(annotationHandler => {
            const viewState = getViewStateForAnnotationHandler(annotationHandler, this.adapter());
            if (viewState) {
                annotationHandler.handlePluginEvent(
                    event,
                    this.editor,
                    viewState,
                    this.charactersBeforeCursor
                );
            }
        });
    }

    // Have each annotation handler run its post-processor after all of the annotations have been processed
    private runAnnotationHandlerPostProcessors(parentPath: string) {
        this.annotationHandlers.forEach(annotationHandler => {
            annotationHandler.onOperationComplete(parentPath, this.editor);
        });
    }

    private submitTextTile() {
        if (!this.editor || !this.augLoopSession) {
            return;
        }

        let operation: IOperation;
        if (this.bodyTileId) {
            operation = new UpdateOperation();
        } else {
            operation = new AddOperation();
            this.bodyTileId = getGuid();
        }

        if (this.charactersBeforeCursor) {
            this.mostRecentTextContentLength = this.charactersBeforeCursor.length;
            const textTileItem = this.createTextTileItem(
                this.charactersBeforeCursor,
                this.bodyTileId
            );
            operation.parentPath = ['session', 'doc'];
            operation.items = [textTileItem];
            this.augLoopSession.submitOperation(operation);
        }
    }

    private createTextTileItem(body: string, id: string): Item {
        const newBody = new FormattedTextTile({ content: body });
        return {
            id: id,
            body: newBody,
        };
    }

    private groupMailboxTypes: string[] = ['PublicDL', 'PrivateDL', 'GroupMailbox', 'PublicFolder'];
    private userMailboxTypes: string[] = [
        'User',
        'Contact',
        'InternalMailbox',
        'ExternalMailbox',
        'OneOff',
        'Mailbox',
        'Guest',
        'ImplicitContact',
        'ChatImplicitContact',
    ];

    private getType(mailboxType: string | undefined): MailboxType {
        if (mailboxType && this.groupMailboxTypes.indexOf(mailboxType) != -1) {
            return MailboxType.GroupMailbox;
        } else if (mailboxType && this.userMailboxTypes.indexOf(mailboxType) != -1) {
            return MailboxType.UserMailbox;
        } else {
            return MailboxType.Unknown;
        }
    }

    private transformEmailAddressWrapperToRecipient(
        emailAddressWrapper: EmailAddressWrapper
    ): Recipient {
        return {
            a: emailAddressWrapper.EmailAddress,
            n: emailAddressWrapper.Name,
            t: this.getType(emailAddressWrapper.MailboxType),
        };
    }

    private createSmartComposeEnvelope(
        subject: string,
        toRecipients: EmailAddressWrapper[],
        user: Recipient,
        constraints: Map<string, string>,
        emailReferenceId: string | undefined
    ): SmartComposeEnvelope {
        const contraintsObj = {};
        constraints.forEach(function (value, key) {
            contraintsObj[key] = value;
        });

        return {
            subject: subject,
            to: toRecipients.map(recipient =>
                this.transformEmailAddressWrapperToRecipient(recipient)
            ),
            user: user,
            constraints: JSON.stringify(contraintsObj),
            emailReferenceId: emailReferenceId,
        };
    }

    private transformEnvelope(envelope: Envelope): SmartComposeEnvelope {
        const constraints = new Map<string, string>();
        constraints.set('compliant', '1');
        addFlightInformationToConstraintsEnUs(constraints);
        // Honeybee intent detection flighting
        addIntentDetectionFlightConstraintsEnUs(constraints);
        const newEnvelope = this.createSmartComposeEnvelope(
            envelope.subject,
            envelope.recipients.toRecipients,
            { a: getUserEmailAddress(), n: getUserDisplayName() },
            constraints,
            envelope.emailReferenceId
        );

        return newEnvelope;
    }

    private updateEnvelope() {
        this.envelope = {
            subject: this.getSubject(),
            recipients: this.getRecipients(),
            emailReferenceId: this.getEmailReferenceId(),
        };
        if (isFeatureEnabled('mc-unified-text-prediction')) {
            const item: Item = {
                id: 'envelope',
                body: new MailDocument({
                    envelope: this.transformEnvelope(this.envelope),
                    isReadonly: true,
                }),
            };
            const operation = new AddOperation({
                parentPath: ['session', MailDocument.getTypeName()],
                items: [item],
            });
            this.augLoopSession?.submitOperation(operation);
        }
    }
}
